import { Button } from '~/components/general/Button'
import { trackEvent } from '~/utils/tracking'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { useEffect } from 'react'
import { createOptimizedContentfulImage } from '~/libs/contentful/helpers'
import { FlexContainer } from '~/components/Grid'
import { TypeHomePageBlock } from '~/types/contentful'
import { Asset } from 'contentful'

gsap.registerPlugin(ScrollTrigger)

type CareerTransitionProps = {
  section: TypeHomePageBlock<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const CareerTransition = ({ section }: CareerTransitionProps) => {
  const data = section.fields

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          start: 'top center',
          trigger: '.block-transition-trigger',
        },
      })
      tl.fromTo(
        '.career-transition-person',
        0.4,
        {
          x: 0,
          y: 0,
        },
        {
          x: 0,
          y: -100,
        },
      )
        .fromTo(
          '.career-transition-card4',
          0.4,
          {
            x: 0,
            y: 0,
          },
          {
            x: 215,
            y: 75,

            height: 'auto',
          },
          '-=0.4',
        )
        .fromTo(
          '.career-transition-card3',
          0.4,
          {
            x: 0,
            y: 0,
          },
          {
            x: 36,
            y: -43,
            height: 'auto',
          },
          '-=0.4',
        )
        .fromTo(
          '.career-transition-card2',
          0.4,
          {
            x: 0,
            y: 0,
          },
          {
            x: -142,
            y: -2,
            height: 'auto',
          },
          '-=0.4',
        )
        .fromTo(
          '.career-transition-card1',
          0.4,
          {
            x: 0,
            y: 0,
          },
          {
            x: -214,
            y: 182,
            height: 'auto',
          },
          '-=0.4',
        )
    })

    return () => ctx.revert()
  }, [])

  const handleCTABtnClick = () => {
    trackEvent('homepage_click_cta', {
      block: data.blockId,
      target: data.buttonLink as string,
      homepage_CTA: data.buttonName as string,
    })
  }

  return (
    <section
      data-block-id={data.blockId}
      id={data.blockId}
      className="min-h-[622px] max-w-[1376px] p-[66px_75px_56px_125px] max-xl:m-[100px_-18px_40px] max-xl:p-[66px_75px_56px_75px] max-md:py-8 max-md:px-2 relative flex overflow-hidden z-2 mx-auto my-25 block-transition-trigger max-md:my-21 max-md:-mx-0 max-lg:my-0 max-lg:flex-col-reverse max-lg:items-center max-lg:gap-5"
      style={{
        backgroundImage: data.images
          ? `url(${createOptimizedContentfulImage({ file: (data.images[0] as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>).fields.file! })})`
          : '',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f4f6fa',
      }}
    >
      <FlexContainer
        className="z-2 max-w-[514px] relative flex-col max-md:gap-0 my-auto mx-0 items-stretch"
        justify={'between'}
        style={{ flexBasis: '55%' }}
      >
        <h2 className="mb-8 max-md:mb-3 text-2xl">{data.title}</h2>
        <p className="text-s mb-10 max-w-[377px] max-xl:mb-9 max-lg:mb-7 max-md:mb-5">
          {data.description}
        </p>
        <Button
          text={data.buttonName}
          type={'link'}
          href={data.buttonLink}
          color={'dark'}
          onClick={handleCTABtnClick}
        />
      </FlexContainer>
      <div className="relative z-1 basis-[57%] w-[40%] pt-11 max-lg:min-h-[450px] max-md:scale-50 max-md:min-h-[350px]">
        <div className="z-1 relative w-full h-full [&_img]:absolute [&_img]:w-[174px] [&_img]:top-0 [&_img]:left-[55%] [&_img]:-translate-x-1/2 [&_img]:h-[210px]">
          <img
            src="/images/career_transition_card1.png"
            className="career-transition-card1 block w-full h-auto"
            alt=""
            loading="lazy"
          />
          <img
            src="/images/career_transition_card2.png"
            className="career-transition-card2 block w-full h-auto"
            alt=""
            loading="lazy"
          />
          <img
            src="/images/career_transition_card3.png"
            className="career-transition-card3 block w-full h-auto"
            alt=""
            loading="lazy"
          />
          <img
            src="/images/career_transition_card4.png"
            className="career-transition-card4 block w-full h-auto"
            alt=""
            loading="lazy"
          />
        </div>

        <div
          className="z-1 absolute left-[55%] bottom-[-8%] max-xl:w-[300px] max-md:bottom-[-33%]"
          style={{ transform: 'translate(-50%, 17%)' }}
        >
          <img
            src="/images/career_transition_celebrate_icon.png"
            className={'career-transition-person block w-full h-auto'}
            alt=""
            loading="lazy"
          />
        </div>
      </div>
    </section>
  )
}
